import React from 'react';
import { graphql } from 'gatsby';

import Layout from '@components/Layout';
import SEO from '@components/SEO';
import Breadcrumbs from '@components/Breadcrumbs';
import Hero from '@components/Hero';
import { Col, Divider, Grid, Row } from '@components/Grid';
import CallToActions from '@components/CallToActions';
import Accordion from '@components/Accordion';
import NewsLetter from '@components/NewsLetter';
import SeasonOffers from '@components/SeasonOffers';
import SeasonThingsToDo from '@components/SeasonThingsToDo';
import { Container } from '@mui/material';

import styles from './styles.module.scss';

const Season = ({
    data: {
        page: {
            frontmatter: {
                path,
                title,
                subtitle,
                seoTitle,
                seoDescription,
                description,
                cardDescription,
                image,
                offers,
                thingsToDoTitle,
                thingsToDoDescription,
                thingsToDoImage,
                thingsToDoButtonText,
                thingsToDoButtonLink,
                secondaryCallToActionTitle,
                secondaryCallToActionDescription,
                secondaryCallToActionButtonText,
                secondaryCallToActionButtonLink,
                secondaryCallToActionImage,
                faqs,
            },
        },
    },
}) => {
    const secondaryCta = {
        title: secondaryCallToActionTitle,
        description: secondaryCallToActionDescription,
        buttonText: secondaryCallToActionButtonText,
        buttonLink: secondaryCallToActionButtonLink,
        image: secondaryCallToActionImage,
    };

    const hasThingsToDo = !!(
        thingsToDoTitle ||
        thingsToDoDescription ||
        thingsToDoImage
    );
    const hasSecondaryCta = !!(
        secondaryCta.title ||
        secondaryCta.description ||
        secondaryCta.image
    );

    return (
        <Layout>
            <SEO
                title={seoTitle || `${title} | Seasons`}
                description={seoDescription || cardDescription}
            />
            <Breadcrumbs>
                <Breadcrumbs.Breadcrumb title="Seasons" link="/seasons" />
                <Breadcrumbs.Breadcrumb title={title} link={path} />
            </Breadcrumbs>

            <Hero image={image} imageFullWidth imageStyle={{ objectPosition: '50% 100%' }}>
                {!!subtitle && <h2>{subtitle}</h2>}
                <h1>{title}</h1>
            </Hero>

            {!!description && (
                <Grid>
                    <Row center="xs">
                        <Col xs={12} md={10}>
                            <div
                                className={styles.richText}
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        </Col>
                    </Row>
                </Grid>
            )}

            {!!offers?.length && (
                <>
                    <Divider margin={3} />
                    <SeasonOffers offers={offers} />
                </>
            )}

            {hasThingsToDo && (
                <>
                    <Divider margin={3} />
                    <SeasonThingsToDo
                        title={thingsToDoTitle}
                        description={thingsToDoDescription}
                        image={thingsToDoImage}
                        buttonText={thingsToDoButtonText}
                        buttonLink={thingsToDoButtonLink}
                    />
                </>
            )}

            {hasSecondaryCta && (
                <>
                    <Divider margin={5} />
                    <CallToActions>
                        <CallToActions.CallToAction {...secondaryCta} />
                    </CallToActions>
                </>
            )}

            {!!faqs?.length && (
                <>
                    <Divider margin={5} />
                    <Container>
                        <h2>Commonly asked questions</h2>
                        {faqs.map(({ question, answer }, index) => (
                            <Accordion key={index} title={question} text={answer} />
                        ))}
                    </Container>
                </>
            )}

            <NewsLetter />
        </Layout>
    );
};

export default Season;

export const query = () => graphql`
    query SeasonQuery($path: String!) {
        page: markdownRemark(frontmatter: { path: { eq: $path } }) {
            frontmatter {
                path
                title: season_title
                subtitle: season_subtitle
                seoTitle: season_seo_title
                seoDescription: season_seo_description
                description: season_description
                cardDescription: season_card_description
                image: season_image {
                    childImageSharp {
                        fluid(maxWidth: 1920) {
                            ...GatsbyImageSharpFluid
                        }
                    }
                }
                offers: season_offers {
                    title: season_offers_title
                    description: season_offers_description
                    link: season_offers_link
                    buttonText: season_offers_button_text
                    image: season_offers_image {
                        childImageSharp {
                            fluid(maxWidth: 800) {
                                ...GatsbyImageSharpFluid
                            }
                        }
                    }
                }
                thingsToDoTitle: season_call_to_action_primary_title
                thingsToDoDescription: season_call_to_action_primary_description
                thingsToDoButtonText: season_call_to_action_primary_button_text
                thingsToDoButtonLink: season_call_to_action_primary_button_link
                thingsToDoImage: season_call_to_action_primary_image {
                    childImageSharp {
                        fluid(maxWidth: 900) {
                            ...GatsbyImageSharpFluid
                        }
                    }
                }
                secondaryCallToActionTitle: season_call_to_action_secondary_title
                secondaryCallToActionDescription: season_call_to_action_secondary_description
                secondaryCallToActionButtonText: season_call_to_action_secondary_button_text
                secondaryCallToActionButtonLink: season_call_to_action_secondary_button_link
                secondaryCallToActionImage: season_call_to_action_secondary_image {
                    childImageSharp {
                        fluid(maxWidth: 1200) {
                            ...GatsbyImageSharpFluid
                        }
                    }
                }
                faqs: season_faqs {
                    question: season_faqs_question
                    answer: season_faqs_answer
                }
            }
        }
    }
`;
